import React, { useState } from 'react';
import { useParams, useNavigate, useOutletContext } from 'react-router-dom';
import { $api } from '../../../api';
import { useOrganization } from '../../../contexts/OrganizationContext';
import { useQueryClient } from '@tanstack/react-query';
import AddRelationModal from './AddRelationModal';
import { IncomingRelationsSection } from './IncomingRelationsSection';
import { OutgoingRelationsSection } from './OutgoingRelationsSection';
import { Relation } from './RelationCard';
import DeleteRelationModal from './DeleteRelationModal';

const EntityStructure: React.FC = () => {
  const { clientId, organizationId } = useParams<{ clientId: string; organizationId: string }>();
  const { currentOrganization } = useOrganization();
  const { isCompany, name } = useOutletContext<{ isCompany: boolean, name: string }>();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const orgId = organizationId || currentOrganization?.id || '';
  const entityId = clientId || '';
  const currentEntityName = name || 'cette entreprise';

  const [isAddRelationModalOpen, setIsAddRelationModalOpen] = useState(false);
  const [isEditingRelation, setIsEditingRelation] = useState<Relation | null>(null);
  const [relationDirection, setRelationDirection] = useState<'incoming' | 'outgoing'>('incoming');
  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);
  const [relationToDelete, setRelationToDelete] = useState<Relation | null>(null);

  // Delete relation mutation
  const deleteRelationMutation = $api.useMutation(
    'delete',
    '/api/organization/{organization_id}/entities/{id}/relations/{relation_id}'
  );

  const handleEntityClick = (entityId: string) => {
    if (orgId) {
      navigate(`/${orgId}/entity/${entityId}`);
    }
  };

  const handleAddRelation = (direction: 'incoming' | 'outgoing') => {
    setIsEditingRelation(null);
    setIsAddRelationModalOpen(true);
    setRelationDirection(direction);
  };

  const handleEditRelation = (relation: Relation) => {
    setIsEditingRelation(relation);
    setIsAddRelationModalOpen(true);
  };

  const handleDeleteClick = (relation: Relation) => {
    setRelationToDelete(relation);
    setIsDeleteModalOpen(true);
  };

  const handleDeleteConfirm = () => {
    if (!relationToDelete) return;

    // The API endpoint uses {id} as the parent entity ID
    // We need to use the parent_entity_id from the relation
    const parentEntityId = relationToDelete.parent_entity_id;

    deleteRelationMutation.mutate(
      {
        params: {
          path: {
            organization_id: orgId,
            id: parentEntityId,
            relation_id: relationToDelete.id,
          },
        },
      },
      {
        onSuccess: () => {
          // Invalidate all relation queries to trigger refetch in child components
          queryClient.invalidateQueries({
            queryKey: ['get', '/api/organization/{organization_id}/entities/{id}/relations'],
          });
          setIsDeleteModalOpen(false);
          setRelationToDelete(null);
        },
      }
    );
  };

  const handleDeleteModalClose = () => {
    if (!deleteRelationMutation.isPending) {
      setIsDeleteModalOpen(false);
      setRelationToDelete(null);
      deleteRelationMutation.reset();
    }
  };

  return (
    <>
      <div className="space-y-6">
        {/* Header */}
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
          <div>
            <h2 className="text-xl font-semibold text-gray-900 mb-1">Structure organisationnelle</h2>
            <p className="text-sm text-gray-600">
              { isCompany ? 'Visualisez les relations et la structure de cette entreprise' : 'Visualisez les relations de cette personne' }
            </p>
          </div>
        </div>

        {/* Incoming Relations Section - Who owns/governs this company */}
        {isCompany && (
          <IncomingRelationsSection
            onAddRelation={() => handleAddRelation('incoming')}
            onEntityClick={handleEntityClick}
            onEditRelation={handleEditRelation}
            onDeleteRelation={handleDeleteClick}
            isDeleting={deleteRelationMutation.isPending}
          />
        )}

        {/* Outgoing Relations Section - Which companies this one owns/shares */}
        <OutgoingRelationsSection
          onAddRelation={() => handleAddRelation('outgoing')}
          onEntityClick={handleEntityClick}
          onEditRelation={handleEditRelation}
          onDeleteRelation={handleDeleteClick}
          isDeleting={deleteRelationMutation.isPending}
        />
      </div>

      {/* Modal d'ajout/modification de relation */}
      <AddRelationModal
        isOpen={isAddRelationModalOpen}
        onClose={() => {
          setIsAddRelationModalOpen(false);
          setIsEditingRelation(null);
        }}
        onSubmit={() => {
          // Invalidate all relation queries to trigger refetch in child components
          queryClient.invalidateQueries({
            queryKey: ['get', '/api/organization/{organization_id}/entities/{id}/relations'],
          });
          setIsAddRelationModalOpen(false);
          setIsEditingRelation(null);
        }}
        editingRelation={isEditingRelation}
        organizationId={orgId}
        entityId={entityId}
        currentEntityName={currentEntityName}
        relationDirection={isEditingRelation ? (isEditingRelation.child_entity_id === entityId ? 'incoming' : 'outgoing') : relationDirection}
      />

      {/* Modal de confirmation de suppression */}
      <DeleteRelationModal
        isOpen={isDeleteModalOpen}
        onClose={handleDeleteModalClose}
        relation={relationToDelete}
        onConfirm={handleDeleteConfirm}
        isDeleting={deleteRelationMutation.isPending}
        error={deleteRelationMutation.error}
      />
    </>
  );
};

export default EntityStructure;

